import { EyeOutlined } from "@ant-design/icons";
import type { IntlShape } from "@formatjs/intl";
import { Button, Card, Checkbox, Flex, Image, Tooltip, Typography } from "antd";
import type { KeyboardEvent, MouseEvent } from "react";
import { FileThumbnail } from "../../../../common/components/FileThumbnail";
import { buildDownloadUrl, buildThumbnailUrl } from "../FilesInfo";
import styles from "./ReportPictureTile.module.css";
import type { ReportPicture } from "./reportPictures";

type ReportPictureTileProps = {
  picture: ReportPicture;
  selected: boolean;
  onToggle: () => void;
  onPreview: () => void;
  intl: IntlShape;
};

export function ReportPictureTile({
  picture,
  selected,
  onToggle,
  onPreview,
  intl,
}: ReportPictureTileProps) {
  const { canBeIncluded } = picture;
  const toggle = () => {
    if (canBeIncluded) onToggle();
  };
  const toggleOnKeyboard = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      toggle();
    }
  };
  const preview = (event: MouseEvent) => {
    event.stopPropagation();
    onPreview();
  };

  const tileClassName = [
    styles.tile,
    selected ? styles.tileSelected : "",
    canBeIncluded ? "" : styles.tileDisabled,
  ].join(" ");

  return (
    <Tooltip
      title={
        canBeIncluded
          ? undefined
          : intl.formatMessage({ id: "wo_report_picture_unsupported" })
      }
    >
      <Card
        size="small"
        hoverable={canBeIncluded}
        className={tileClassName}
        role="checkbox"
        aria-checked={selected}
        aria-disabled={!canBeIncluded}
        aria-label={picture.caption}
        tabIndex={canBeIncluded ? 0 : -1}
        onClick={toggle}
        onKeyDown={toggleOnKeyboard}
        cover={
          <Flex align="center" justify="center" className={styles.cover}>
            {canBeIncluded ? (
              // thumbnails are generated after upload, so the full picture stands in
              // until one exists
              <Image
                src={buildThumbnailUrl({ id: picture.fileId })}
                fallback={buildDownloadUrl({ id: picture.fileId })}
                alt={picture.name}
                preview={false}
              />
            ) : (
              <FileThumbnail
                contentType={picture.contentType}
                name={picture.name}
                size={48}
              />
            )}
          </Flex>
        }
      >
        <Flex align="center" gap="small">
          <Checkbox
            checked={selected}
            disabled={!canBeIncluded}
            tabIndex={-1}
            className={styles.checkbox}
          />
          <Typography.Text
            ellipsis={{ tooltip: picture.caption }}
            className={styles.caption}
          >
            {picture.caption}
          </Typography.Text>
          <Button
            type="text"
            size="small"
            icon={<EyeOutlined />}
            onClick={preview}
            aria-label={intl.formatMessage({ id: "ticket_file_preview" })}
          />
        </Flex>
      </Card>
    </Tooltip>
  );
}
